import React, { useRef } from "react";
import { getApiUrl, getRelativeUrl } from "../../utils/urlHelper.js";
import { useModalAccessibility } from "../../hooks/useModalAccessibility";

const PhotoLightboxModal = ({
  isModalOpen,
  previewPhoto,
  currentImageIndex,
  isHighResLoaded,
  setIsHighResLoaded,
  handleModalClose,
  handlePrevImage,
  handleNextImage,
  handleDownloadCurrentImage,
  handleShowCode,
  getDisplayedPhotos,
  getThumbUrl,
  getPreviewUrl,
  getBrowserSafeUrl,
  groupData,
  selectedPhotos,
  togglePhotoSelection,
}) => {
  const modalRef = useRef(null);
  const [imgNaturalSize, setImgNaturalSize] = React.useState({ w: 0, h: 0 });
  const [renderedSize, setRenderedSize] = React.useState({ w: "100vw", h: "100vh" });

  React.useEffect(() => {
    setImgNaturalSize({ w: 0, h: 0 });
  }, [currentImageIndex]);

  const updateRenderedSize = React.useCallback(() => {
    if (!imgNaturalSize.w || !imgNaturalSize.h) {
      setRenderedSize({ w: "100vw", h: "100vh" });
      return;
    }
    const containerW = window.innerWidth;
    const containerH = window.innerHeight;
    const imgRatio = imgNaturalSize.w / imgNaturalSize.h;
    const containerRatio = containerW / containerH;
    
    if (imgRatio > containerRatio) {
      setRenderedSize({ w: `${containerW}px`, h: `${containerW / imgRatio}px` });
    } else {
      setRenderedSize({ w: `${containerH * imgRatio}px`, h: `${containerH}px` });
    }
  }, [imgNaturalSize]);

  React.useEffect(() => {
    updateRenderedSize();
    window.addEventListener("resize", updateRenderedSize);
    return () => window.removeEventListener("resize", updateRenderedSize);
  }, [updateRenderedSize]);

  useModalAccessibility({
    isOpen: isModalOpen,
    onClose: handleModalClose,
    modalRef,
  });

  if (!isModalOpen || !previewPhoto) return null;

  const currentPhoto = getDisplayedPhotos()[currentImageIndex];
  const photoId = currentPhoto?._id || currentPhoto?.filePath;
  const isSelected = selectedPhotos?.has(photoId);

  return (
    <div
      className="modal-overlay1"
      onClick={handleModalClose}
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-label="Photo Lightbox"
      tabIndex={-1}
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 999999,
        outline: "none",
      }}
    >
      {/* Top Left: Back Arrow and Counter */}
      <div
        style={{
          position: "absolute",
          top: "30px",
          left: "30px",
          display: "flex",
          alignItems: "center",
          gap: "15px",
          color: "white",
          zIndex: 10,
        }}
      >
        <button
          onClick={handleModalClose}
          aria-label="Close"
          style={{
            background: "none",
            border: "none",
            color: "white",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            padding: 0,
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
        </button>
        <span style={{ fontSize: "16px", fontWeight: "500", letterSpacing: "0.5px" }}>
          {currentImageIndex + 1} / {getDisplayedPhotos().length}
        </span>
      </div>


      {/* Previous Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handlePrevImage();
        }}
        aria-label="Previous image"
        style={{
          position: "absolute",
          left: "20px",
          top: "50%",
          transform: "translateY(-50%)",
          background: "rgba(0,0,0,0.5)",
          border: "none",
          color: "white",
          cursor: "pointer",
          zIndex: 10,
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>

      {/* Next Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleNextImage();
        }}
        aria-label="Next image"
        style={{
          position: "absolute",
          right: "20px",
          top: "50%",
          transform: "translateY(-50%)",
          background: "rgba(0,0,0,0.5)",
          border: "none",
          color: "white",
          cursor: "pointer",
          zIndex: 10,
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>

      {/* Top Right Icons */}
      <div
        style={{
          position: "absolute",
          right: "30px",
          top: "30px",
          display: "flex",
          flexDirection: "column",
          gap: "25px",
          zIndex: 10,
        }}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (togglePhotoSelection && photoId) {
              togglePhotoSelection(photoId);
            }
          }}
          style={{ 
            background: isSelected ? "#7c3aed" : "rgba(0,0,0,0.4)", 
            border: isSelected ? "2px solid #ffffff" : "2px solid rgba(255,255,255,0.7)", 
            color: "white", 
            cursor: "pointer", 
            padding: 0,
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease"
          }}
          title={isSelected ? "Deselect" : "Select"}
          aria-label="Select Photo"
        >
          {isSelected ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          ) : (
            <div style={{ width: "16px", height: "16px", borderRadius: "50%" }}></div>
          )}
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDownloadCurrentImage();
          }}
          style={{ background: "none", border: "none", color: "white", cursor: "pointer", padding: 0 }}
          title="Download"
          aria-label="Download"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
        </button>
      </div>

      {/* Bottom Center Filename */}
      <div
        style={{
          position: "absolute",
          bottom: "30px",
          left: "50%",
          transform: "translateX(-50%)",
          color: "rgba(255,255,255,0.7)",
          fontSize: "13px",
          zIndex: 10,
          fontFamily: "monospace",
        }}
      >
        {previewPhoto.fileName || `Photo ${currentImageIndex + 1}`}
      </div>

      {/* Main Content */}
      <div
        className="modal-content1"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "transparent",
        }}
      >
        <div 
          className="modal-img-wrapper" 
          style={{ width: renderedSize.w, height: renderedSize.h }}
        >
          {/* Blurred Placeholder (thumbnail - tiny, loads instantly) */}
          <img
            src={getThumbUrl(previewPhoto)}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = getBrowserSafeUrl(previewPhoto.filePath);
            }}
            alt={previewPhoto.fileName || "Preview Thumbnail"}
            className="modal-img"
            style={{
              filter: "blur(15px)",
              opacity: isHighResLoaded ? 0 : 1,
              transition: "opacity 0.4s ease-in-out",
              transform: "scale(1.05)",
              maxHeight: "100vh",
              maxWidth: "100vw",
              objectFit: "contain",
            }}
          />
          {/* 1920px WebP Preview — Sharp optimized, HD quality, fast load */}
          <img
            src={getPreviewUrl(previewPhoto)}
            onError={(e) => {
              e.target.onerror = null;
              // Fallback to original if Sharp fails
              e.target.src = getBrowserSafeUrl(previewPhoto.filePath);
            }}
            onLoad={(e) => {
              setIsHighResLoaded(true);
              setImgNaturalSize({ w: e.target.naturalWidth, h: e.target.naturalHeight });
            }}
            alt={previewPhoto.fileName || "Preview"}
            className="modal-img"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              opacity: isHighResLoaded ? 1 : 0,
              transition: "opacity 0.4s ease-in-out",
              maxHeight: "100vh",
              maxWidth: "100vw",
              objectFit: "contain",
            }}
          />

          {/* Watermark Overlay in Lightbox Modal */}
          {(groupData.watermark?.enabled ?? true) && (
            (groupData.watermark?.position || "bottom-right") === "tile" ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gridTemplateRows: "repeat(3, 1fr)",
                  gap: "8px",
                  padding: "16px",
                  pointerEvents: "none",
                  overflow: "hidden",
                }}
              >
                {[...Array(9)].map((_, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img
                      src={getApiUrl(getRelativeUrl(groupData.watermark?.image || "/images/logo22.png"))}
                      alt="WM"
                      style={{
                        width: `${(groupData.watermark.scale || 20) * 1.5}%`,
                        opacity: (groupData.watermark.opacity || 70) / 100,
                        objectFit: "contain",
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  position: "absolute",
                  left: `${groupData.watermark.posX ?? 85}%`,
                  top: `${groupData.watermark.posY ?? 85}%`,
                  width: `${groupData.watermark.scale || 20}%`,
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              >
                <img
                  src={getApiUrl(getRelativeUrl(groupData.watermark?.image || "/images/logo22.png"))}
                  alt="Watermark"
                  style={{
                    width: "100%",
                    height: "auto",
                    opacity: (groupData.watermark.opacity || 70) / 100,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoLightboxModal;
